import type { Tone } from "@amtana/design-system";
import type { Catalogs, Formats, PlainKey, Translation } from "@amtana/i18n/catalog";
import type { Category, Folder } from "@amtana/inbox-contract";

// Every word the inbox shows, in English and Spanish, one key per line. A table
// of labels holds keys, not words; the screen reads them through `useInboxCopy`.

export type InboxCopyKey = keyof typeof EN;
export type InboxCatalog = typeof EN;

const EN = {
  screenTitle: "Inbox · {product}",
  navInbox: "Inbox",
  breadcrumbHome: "Home",
  pageTitle: "Inbox",
  pageDescription: "Every reply to your campaigns, in one place.",
  folderInbox: "Inbox",
  folderStarred: "Starred",
  folderSent: "Sent",
  folderArchived: "Archived",
  folderTrash: "Trash",
  folderWithCount: "{folder} ({count})",
  categoryInterested: "Interested",
  categoryNotInterested: "Not interested",
  categoryOutOfOffice: "Out of office",
  categoryWrongPerson: "Wrong person",
  qualificationDoNotContact: "Do not contact",
  scheduleInOneHour: "In 1 hour",
  scheduleInTwoHours: "In 2 hours",
  scheduleInFourHours: "In 4 hours",
  noAccountsTitle: "No inbox accounts configured",
  noAccountsDescription: "Enable inbox functionality for your email accounts to start managing conversations",
  goToEmailAccounts: "Go to Email Accounts",
  reconnectTitle: "Reconnect your mailbox.",
  reconnectOne:
    "We can't read replies for {email} because its inbox connection failed. New replies won't appear until you reconnect it.",
  reconnectMany:
    "{count} mailboxes have a failed inbox connection, so their new replies won't appear until you reconnect them.",
  folderLabel: "Folder",
  selectAccount: "Select account",
  allAccounts: "All accounts",
  searchPlaceholder: "Search emails...",
  conversationsLabel: "Conversations",
  tabAll: "All",
  tabReplied: "Replied",
  tabConversations: "Conversations",
  unread: "Unread",
  unreadWithCount: "Unread ({count})",
  noConversations: "No conversations found",
  selectConversation: "Select a conversation to view",
  loading: "Loading…",
  replyTo: "Reply to {name}",
  replyPlaceholder: "Type your reply...",
  whenToSend: "When to send",
  sendNow: "Send now",
  send: "Send",
  schedule: "Schedule",
  campaignVia: "{campaign} · via {email}",
  backToInbox: "Back to inbox",
  star: "Star",
  unstar: "Unstar",
  threadActions: "Thread Actions",
  markAsUnread: "Mark as unread",
  markAsRead: "Mark as read",
  restoreToInbox: "Restore to Inbox",
  archive: "Archive",
  delete: "Delete",
  you: "You",
  scheduledFor: "Scheduled for {when}",
  starred: "Starred",
  unstarred: "Unstarred",
  markedAsUnread: "Marked as unread",
  markedAsRead: "Marked as read",
  restoredToInbox: "Restored to Inbox",
  archived: "Archived",
  deleted: "Deleted",
  replyScheduled: "Reply scheduled",
  replySent: "Reply sent successfully",
  couldNotMarkRead: "Could not mark the conversation as read",
  couldNotSendReply: "Could not send the reply",
  couldNotUpdateConversation: "Could not update the conversation",
  conversationDidNotLoad: "This conversation did not load",
  contactEmptyTitle: "Contact information will appear here",
  leadCategory: "Lead category",
  uncategorized: "Uncategorized",
  qualification: "Qualification",
  qualificationUpdated: "Qualification updated",
  couldNotUpdateQualification: "Could not update the qualification",
  personalInformation: "Personal Information",
  email: "Email",
  phone: "Phone",
  website: "Website",
  followers: "Followers",
  notAProspect: "This contact is not one of your prospects, so there is nothing enriched to show.",
  lookingUpContact: "Looking up this contact…",
} as const;

const ES: Translation<InboxCatalog> = {
  screenTitle: "Bandeja de entrada · {product}",
  navInbox: "Bandeja de entrada",
  breadcrumbHome: "Inicio",
  pageTitle: "Bandeja de entrada",
  pageDescription: "Todas las respuestas a tus campañas, en un solo lugar.",
  folderInbox: "Bandeja de entrada",
  folderStarred: "Destacados",
  folderSent: "Enviados",
  folderArchived: "Archivados",
  folderTrash: "Papelera",
  folderWithCount: "{folder} ({count})",
  categoryInterested: "Interesado",
  categoryNotInterested: "No interesado",
  categoryOutOfOffice: "Fuera de la oficina",
  categoryWrongPerson: "Persona equivocada",
  qualificationDoNotContact: "No contactar",
  scheduleInOneHour: "En 1 hora",
  scheduleInTwoHours: "En 2 horas",
  scheduleInFourHours: "En 4 horas",
  noAccountsTitle: "No hay cuentas de bandeja de entrada configuradas",
  noAccountsDescription:
    "Activa la bandeja de entrada en tus cuentas de correo para empezar a gestionar conversaciones",
  goToEmailAccounts: "Ir a cuentas de correo",
  reconnectTitle: "Reconecta tu buzón.",
  reconnectOne:
    "No podemos leer las respuestas de {email} porque falló la conexión de su bandeja de entrada. Las nuevas respuestas no aparecerán hasta que la reconectes.",
  reconnectMany:
    "{count} buzones tienen la conexión de la bandeja de entrada fallida, así que sus nuevas respuestas no aparecerán hasta que los reconectes.",
  folderLabel: "Carpeta",
  selectAccount: "Selecciona una cuenta",
  allAccounts: "Todas las cuentas",
  searchPlaceholder: "Buscar correos...",
  conversationsLabel: "Conversaciones",
  tabAll: "Todas",
  tabReplied: "Respondidas",
  tabConversations: "Conversaciones",
  unread: "Sin leer",
  unreadWithCount: "Sin leer ({count})",
  noConversations: "No se encontraron conversaciones",
  selectConversation: "Selecciona una conversación para verla",
  loading: "Cargando…",
  replyTo: "Responder a {name}",
  replyPlaceholder: "Escribe tu respuesta...",
  whenToSend: "Cuándo enviar",
  sendNow: "Enviar ahora",
  send: "Enviar",
  schedule: "Programar",
  campaignVia: "{campaign} · vía {email}",
  backToInbox: "Volver a la bandeja de entrada",
  star: "Destacar",
  unstar: "Quitar destacado",
  threadActions: "Acciones de la conversación",
  markAsUnread: "Marcar como sin leer",
  markAsRead: "Marcar como leída",
  restoreToInbox: "Restaurar a la bandeja de entrada",
  archive: "Archivar",
  delete: "Eliminar",
  you: "Tú",
  scheduledFor: "Programado para {when}",
  starred: "Destacada",
  unstarred: "Destacado quitado",
  markedAsUnread: "Marcada como sin leer",
  markedAsRead: "Marcada como leída",
  restoredToInbox: "Restaurada a la bandeja de entrada",
  archived: "Archivada",
  deleted: "Eliminada",
  replyScheduled: "Respuesta programada",
  replySent: "Respuesta enviada correctamente",
  couldNotMarkRead: "No se pudo marcar la conversación como leída",
  couldNotSendReply: "No se pudo enviar la respuesta",
  couldNotUpdateConversation: "No se pudo actualizar la conversación",
  conversationDidNotLoad: "Esta conversación no se cargó",
  contactEmptyTitle: "La información del contacto aparecerá aquí",
  leadCategory: "Categoría del lead",
  uncategorized: "Sin categoría",
  qualification: "Calificación",
  qualificationUpdated: "Calificación actualizada",
  couldNotUpdateQualification: "No se pudo actualizar la calificación",
  personalInformation: "Información personal",
  email: "Correo",
  phone: "Teléfono",
  website: "Sitio web",
  followers: "Seguidores",
  notAProspect: "Este contacto no es uno de tus prospectos, así que no hay datos enriquecidos para mostrar.",
  lookingUpContact: "Buscando este contacto…",
};

export const INBOX_CATALOGS: Catalogs<InboxCatalog> = { en: EN, es: ES };

type Label = PlainKey<InboxCatalog>;

export const FOLDERS: ReadonlyArray<{ value: Folder; label: Label }> = [
  { value: "inbox", label: "folderInbox" },
  { value: "starred", label: "folderStarred" },
  { value: "sent", label: "folderSent" },
  { value: "archived", label: "folderArchived" },
  { value: "trash", label: "folderTrash" },
];

export const CATEGORY: Readonly<Record<Category, { label: Label; tone: Tone }>> = {
  interested: { label: "categoryInterested", tone: "success" },
  "not-interested": { label: "categoryNotInterested", tone: "danger" },
  "out-of-office": { label: "categoryOutOfOffice", tone: "neutral" },
  "wrong-person": { label: "categoryWrongPerson", tone: "warning" },
};

export const QUALIFICATIONS = [
  { value: "interested", label: "categoryInterested" },
  { value: "not-interested", label: "categoryNotInterested" },
  { value: "wrong-person", label: "categoryWrongPerson" },
  { value: "do-not-contact", label: "qualificationDoNotContact" },
  { value: "out-of-office", label: "categoryOutOfOffice" },
] as const satisfies ReadonlyArray<{ value: string; label: Label }>;

export const SCHEDULE_OPTIONS = [
  { value: "60", label: "scheduleInOneHour" },
  { value: "120", label: "scheduleInTwoHours" },
  { value: "240", label: "scheduleInFourHours" },
] as const satisfies ReadonlyArray<{ value: string; label: Label }>;

/** A time today, a short date otherwise, in the active language. */
export function when(iso: string, format: Formats): string {
  const date = new Date(iso);
  const today = new Date().toDateString() === date.toDateString();
  return today ? format.time(date) : format.date(date, { day: "numeric", month: "short" });
}
